import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  orElse, isNum, fx, fmtN, sgn, rankText, DeltaRank, DeltaPct,
  parseIso, isoOf, ymd, md, dayLabel, meanR, countR, sumR,
  minR, movingAverage, pct, niceMax
} from './analytics/format';
import { StarButton, StarDialog } from './analytics/star';

// MEO分析の店舗ダッシュボード。形は依頼者の画面案（2026-10 受領）のとおり。
//
// この店の2年ぶんの数字（日ごとの配列）を最初に受け取り、表示期間・上部の数字の期間・
// 比較の基準・評価窓・グラフに出すKW を変えても、計算はすべてこの中でやり直す。
// サーバーに聞きに行くのは、ほかの店舗と比べる値（同業種・同じ契約会社・全店舗）だけ。
//
// グラフは Chart.js ではなく SVG で描いている。共通レイアウトの <html> に zoom:80% が
// かかっていて、Chart.js 内蔵の当たり判定はカーソルの位置とずれる（doc/meo_analytics.md）。
// SVG なら日ごとに透明な四角を置いて、ブラウザ自身の当たり判定に任せられる。
// 吹き出しの位置も座標ではなく「何日目か」の割合で決めるので、zoom の影響を受けない。

const KW_COLORS = ['#0d366b', '#184f95', '#2a78d6', '#5598e7', '#86b6ef', '#86b6ef', '#86b6ef'];
const KW_WIDTHS = [2.4, 1.7, 1.6, 1.5, 1.4, 1.4, 1.4];
const AVG_COLOR = '#495057';
const CMP_COLOR = '#adb5bd';

const KPI_PERIODS = [
  ['7d', '過去7日間'], ['30d', '過去30日間'], ['month', '当月'], ['prevmonth', '前月'], ['range', '表示期間全体']
];
const KPI_LABEL = Object.fromEntries(KPI_PERIODS);

const OWN_BASES = [
  ['prev', '前期間（直前の同じ日数）'],
  ['yoy', '前年同期'],
  ['all', '全期間の平均', 'データのある期間（最大2年）の1日平均と比べる']
];
const GROUP_BASES = ['industry', 'company', 'allstores'];
const GROUP_PARAM = { industry: 'industry', company: 'company', allstores: 'all' };

// ---- 本体 ------------------------------------------------------------------

export default function StoreDashboard(props) {
  const { dates, keywords, measures, notes, kinds, urls, outOfRank, starKinds } = props;
  // ★はこの画面からも付け外しできる（保存したらここだけ書き換える）
  const [store, setStore] = useState(props.store);
  const [starOpen, setStarOpen] = useState(false);
  const END = dates.length - 1;
  const indexOf = useMemo(() => Object.fromEntries(dates.map((d, i) => [d, i])), [dates]);
  const provisionalIndex = props.provisionalFrom && indexOf[props.provisionalFrom] !== undefined
    ? indexOf[props.provisionalFrom] : (props.provisionalFrom && props.provisionalFrom < dates[0] ? 0 : null);
  const kindOf = useMemo(() => Object.fromEntries(kinds.map((k) => [k.label, k])), [kinds]);
  const defaultWindows = () => Object.fromEntries(kinds.map((k) => [k.label, { pre: k.window, post: k.window }]));

  // この店の配列。アクション数は3つの合計（UU数が無い日は null）。
  const own = useMemo(() => {
    const s = props.series;
    const act = s.uu.map((uu, i) => (isNum(uu) ? (s.call[i] || 0) + (s.route[i] || 0) + (s.site[i] || 0) : null));
    const kw = {};
    keywords.forEach((k) => { kw[k.no] = s.kw[k.no] || s.kw[String(k.no)] || dates.map(() => null); });
    // kw2・kw3… の名前でも引けるようにしておく（前期間・前年同期と比べるときに KW ごとに使う）。
    // kw1 は「最優先のKW」（登録の先頭）。ほかの店舗の KW1 と比べるのもこれ。
    const byName = Object.fromEntries(keywords.map((k) => [`kw${k.no}`, kw[k.no]]));
    return { uu: s.uu, call: s.call, route: s.route, site: s.site, act, kw, ...byName, kw1: keywords.length ? kw[keywords[0].no] : null, kwavg: s.kwavg };
  }, [props.series, keywords, dates]);
  const hasRank = keywords.length > 0 && own.kwavg.some(isNum);
  const firstKw = keywords.length ? keywords[0] : null;

  // ?from=&to=&sel=（yyyy-mm-dd）で開いたときはその期間・その日を選ぶ（施策の効果分析の事例から開く）
  const linked = useMemo(() => {
    const q = {};
    try { new URLSearchParams(window.location.search).forEach((v, k) => { q[k] = v; }); } catch (e) { /* 古いブラウザは無視 */ }
    const at = (v) => (v && indexOf[v] !== undefined ? indexOf[v] : (v && v < dates[0] ? 0 : (v && v > dates[END] ? END : null)));
    return { from: at(q.from), to: at(q.to), sel: q.sel && indexOf[q.sel] !== undefined ? indexOf[q.sel] : null };
  }, []);
  const [from, setFrom] = useState(orElse(linked.from, Math.max(0, END - 89)));
  const [to, setTo] = useState(orElse(linked.to, END));
  const [kpi, setKpi] = useState('30d');
  const [cmp, setCmp] = useState('yoy');
  const [mode, setMode] = useState('ma');
  const [kws, setKws] = useState(firstKw ? ['avg', firstKw.no] : ['avg']);
  const [windows, setWindows] = useState(defaultWindows);
  const [sel, setSel] = useState(linked.sel);
  const [hover, setHover] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(null);
  const [memoOf, setMemoOf] = useState(null);
  const [groups, setGroups] = useState({});   // basis => { status, data }
  const [toast, setToast] = useState(null);

  // ほかの店舗の値を読む。同業種は対策の「同業種との差」でも使うので必ず読む。
  const loadGroup = (basis) => {
    if (groups[basis]) return;
    setGroups((g) => ({ ...g, [basis]: { status: 'loading' } }));
    const q = `basis=${GROUP_PARAM[basis]}&from=${dates[0]}&to=${dates[END]}`;
    fetch(`${urls.comparison}?${q}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((json) => {
        if (!json.available) {
          setGroups((g) => ({ ...g, [basis]: { status: 'na', reason: json.reason } }));
          return;
        }
        const arr = (map) => dates.map((d) => (map && isNum(map[d]) ? map[d] : null));
        const data = {
          label: json.label, stores: json.stores,
          uu: arr(json.uu), act: arr(json.act), call: arr(json.call), route: arr(json.route), site: arr(json.site),
          kw1: arr(json.kw1), kwavg: arr(json.kwavg)
        };
        setGroups((g) => ({ ...g, [basis]: { status: 'ok', data } }));
      })
      .catch(() => setGroups((g) => ({ ...g, [basis]: { status: 'na', reason: '読み込めませんでした' } })));
  };
  useEffect(() => { loadGroup('industry'); }, []);
  useEffect(() => { if (GROUP_BASES.includes(cmp)) loadGroup(cmp); }, [cmp]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const industry = groups.industry && groups.industry.status === 'ok' ? groups.industry.data : null;

  // ---- 上部の数字の期間と比較 ----
  const kpiRange = useMemo(() => {
    const end = parseIso(dates[END]);
    const y = end.getUTCFullYear(); const m = end.getUTCMonth();
    const idx = (date) => Math.max(0, Math.min(END, orElse(indexOf[isoOf(date)], isoOf(date) < dates[0] ? 0 : END)));
    switch (kpi) {
      case '7d': return [Math.max(0, END - 6), END];
      case '30d': return [Math.max(0, END - 29), END];
      case 'month': return [idx(new Date(Date.UTC(y, m, 1))), END];
      case 'prevmonth': return [idx(new Date(Date.UTC(y, m - 1, 1))), idx(new Date(Date.UTC(y, m, 0)))];
      default: return [from, to];
    }
  }, [kpi, from, to, dates, END, indexOf]);

  // 比較の基準。source は比べる相手の配列、off はずらす日数、flat は全期間の平均。
  const comparison = useMemo(() => {
    const [a, b] = kpiRange; const len = b - a + 1;
    if (cmp === 'prev') return { label: '前期間', source: own, off: -len, store: true, range: a - len >= 0 ? `${ymd(dates[a - len])}〜${ymd(dates[a - 1])}` : null };
    if (cmp === 'yoy') return { label: '前年同期', source: own, off: -365, store: true, range: a - 365 >= 0 ? `${ymd(dates[a - 365])}〜${ymd(dates[b - 365])}` : null };
    if (cmp === 'all') return { label: '全期間の平均', source: own, off: 0, flat: true, store: true, range: `${ymd(dates[0])}〜${ymd(dates[END])}の1日平均×${len}日` };
    const g = groups[cmp];
    const fallback = { industry: '同業種の平均', company: '同じ契約会社の他店舗', allstores: '全店舗の平均' }[cmp];
    if (!g || g.status === 'loading') return { label: fallback, na: '読み込み中…' };
    if (g.status === 'na') return { label: fallback, na: g.reason };
    return { label: g.data.label, source: g.data, off: 0, store: false, range: `同じ期間 ・ ${g.data.stores}店` };
  }, [cmp, kpiRange, own, groups, dates, END]);

  // 比べる相手の値。足りない（半分も取れていない）ときは null。
  const cmpValue = (key, a, b, kind) => {
    const C = comparison;
    if (C.na || !C.source || !C.source[key]) return null;
    const arr = C.source[key]; const len = b - a + 1;
    if (C.flat) {
      const m = meanR(arr, 0, END);
      if (!isNum(m)) return null;
      return kind === 'sum' ? m * len : m;
    }
    const a2 = a + C.off; const b2 = b + C.off;
    if (a2 < 0 || countR(arr, a2, b2) < len / 2) return null;
    return kind === 'sum' ? meanR(arr, a2, b2) * len : meanR(arr, a2, b2);
  };

  // ---- 評価窓で前後を比べる ----
  const beforeAfter = (arr, d, w) => {
    const before = meanR(arr, d - w.pre, d - 1);
    const after = meanR(arr, d + 1, Math.min(d + w.post, END));
    return { before, after, done: d + w.post <= END, left: Math.max(0, d + w.post - END) };
  };

  const measureList = useMemo(() => measures
    .map((m) => ({ ...m, d: indexOf[m.date] }))
    .filter((m) => m.d !== undefined), [measures, indexOf]);
  const noteList = useMemo(() => notes.map((n) => ({
    ...n,
    a: n.start < dates[0] ? 0 : orElse(indexOf[n.start], END),
    b: n.end > dates[END] ? END : orElse(indexOf[n.end], 0)
  })), [notes, indexOf, dates, END]);

  const inRange = measureList.filter((m) => m.d >= from && m.d <= to);

  // 選択日。決まっていなければ、評価の終わった最後の対策の日（無ければ最後の対策・表示期間の最後）。
  const selected = useMemo(() => {
    if (sel !== null && sel >= from && sel <= to) return sel;
    const done = inRange.filter((m) => m.d + windows[m.kind].post <= END);
    if (done.length) return done[done.length - 1].d;
    if (inRange.length) return inRange[inRange.length - 1].d;
    return to;
  }, [sel, from, to, inRange, windows, END]);

  const flagsOf = (m, w) => {
    const out = [];
    const overlap = measureList.filter((x) => x.id !== m.id && x.d >= m.d - w.pre && x.d <= m.d + w.post).length;
    if (overlap) out.push(`評価窓が他の対策と${overlap}件重なる`);
    const excluded = noteList.filter((n) => n.excluded && n.a <= m.d + w.post && n.b >= m.d - w.pre);
    if (excluded.length) out.push(`外部要因により除外（${excluded.map((n) => n.title).join('・')}）`);
    if (provisionalIndex !== null && m.d + w.post >= provisionalIndex) out.push('後の期間に暫定値（GBPの確定待ち）を含む');
    return out;
  };

  // ---- 期間の操作 ----
  const setRange = (a, b) => {
    const na = Math.max(0, Math.min(a, END));
    const nb = Math.max(na, Math.min(b, END));
    setFrom(na); setTo(nb); setSel(null);
  };
  const quick = (q) => {
    const end = parseIso(dates[END]);
    const y = end.getUTCFullYear(); const m = end.getUTCMonth();
    const idx = (date) => { const s = isoOf(date); return s < dates[0] ? 0 : orElse(indexOf[s], END); };
    if (q === '30') setRange(END - 29, END);
    else if (q === '90') setRange(END - 89, END);
    else if (q === 'month') setRange(idx(new Date(Date.UTC(y, m, 1))), END);
    else if (q === 'prev') setRange(idx(new Date(Date.UTC(y, m - 1, 1))), idx(new Date(Date.UTC(y, m, 0))));
    else setRange(idx(new Date(Date.UTC(y, 0, 1))), END);
  };
  const onDate = (which, value) => {
    if (!value) return;
    const i = value < dates[0] ? 0 : value > dates[END] ? END : indexOf[value];
    if (i === undefined) return;
    if (which === 'from') setRange(i, Math.max(i, to)); else setRange(Math.min(from, i), i);
  };

  const toggleKw = (k) => {
    setKws((cur) => {
      if (cur.includes(k)) {
        if (cur.length === 1) { setToast('少なくとも1つはグラフに出します'); return cur; }
        return cur.filter((x) => x !== k);
      }
      return [...cur, k];
    });
  };

  const onStore = (value) => {
    const v = value.trim();
    if (!v) return;
    const hit = (props.storeOptions || []).find((o) => o.label === v);
    if (hit) { window.location.href = `/analytics/${hit.id}`; return; }
    window.location.href = `${urls.find}?q=${encodeURIComponent(v)}`;
  };

  const csvHref = `${urls.csv}?from=${dates[from]}&to=${dates[to]}`;
  // 分析メモ（2026-10-16 保留）。再開するときに戻す
  // const noteHref = (d) => `${urls.newNote}?contract_id=${store.id}&date=${dates[d]}&return_to=${encodeURIComponent(`/analytics/${store.id}`)}`;

  return (
    <div className="sd">
      <StoreHead store={store} onStar={() => setStarOpen(true)} />

      <div className="sd-controls">
        <label className="sd-ctl">店舗
          <input type="text" className="sd-wide" list="sd-store-list" placeholder="店舗名・Aコードで検索"
                 defaultValue={[store.code, store.name].filter(Boolean).join(' ')}
                 onKeyDown={(e) => { if (e.key === 'Enter') onStore(e.target.value); }}
                 onChange={(e) => { if ((props.storeOptions || []).some((o) => o.label === e.target.value)) onStore(e.target.value); }} />
          <datalist id="sd-store-list">
            {(props.storeOptions || []).map((o) => <option key={o.id} value={o.label} />)}
          </datalist>
        </label>
        <span className="sd-ctl">表示期間
          <input type="date" value={dates[from]} min={dates[0]} max={dates[END]} onChange={(e) => onDate('from', e.target.value)} aria-label="表示期間の開始日" />
          〜
          <input type="date" value={dates[to]} min={dates[0]} max={dates[END]} onChange={(e) => onDate('to', e.target.value)} aria-label="表示期間の終了日" />
        </span>
        <span className="sd-seg">
          {[['30', '過去30日'], ['90', '過去90日'], ['month', '当月'], ['prev', '前月'], ['year', '今年']].map(([q, l]) => (
            <button key={q} type="button" onClick={() => quick(q)}>{l}</button>
          ))}
        </span>
        <span className="sd-ctl">表示
          <span className="sd-seg">
            <button type="button" aria-pressed={mode === 'ma'} onClick={() => setMode('ma')}>7日移動平均</button>
            <button type="button" aria-pressed={mode === 'raw'} onClick={() => setMode('raw')}>実測値</button>
          </span>
        </span>
        <span className="sd-ctl-right">
          <button type="button" className="sd-btn" onClick={() => setSettingsOpen('top')}>⚙ 表示設定</button>
          <a className="sd-btn" href={csvHref}>⤓ CSV</a>
        </span>
      </div>

      <div className="sd-kpi-desc">
        <span>上部の数字：<b>{KPI_LABEL[kpi]}</b>（{ymd(dates[kpiRange[0]])}〜{ymd(dates[kpiRange[1]])}）</span>
        <span>比較：<b>{comparison.label}</b>（{comparison.na || comparison.range || 'データがありません'}）</span>
        <button type="button" className="sd-linkbtn" onClick={() => setSettingsOpen('kpi')}>⚙ 変える</button>
      </div>

      <div className="sd-dash">
        <div className="sd-dash-main">
          <Kpis own={own} hasRank={hasRank} firstKw={firstKw} keywords={keywords} kpiRange={kpiRange}
                comparison={comparison} cmpValue={cmpValue} measureList={measureList} from={from} to={to}
                outOfRank={outOfRank} provisionalIndex={provisionalIndex} />

          <div className="sd-card">
            <header><h2>KW別の順位</h2><span className="sd-sub">平均順位の内訳。KW1がいちばん優先度の高いキーワードです</span></header>
            <div className="sd-body sd-flush">
              {hasRank ? (
                <KwTable own={own} keywords={keywords} kws={kws} toggleKw={toggleKw} kpi={kpi} kpiRange={kpiRange}
                         comparison={comparison} cmpValue={cmpValue} from={from} to={to} outOfRank={outOfRank} />
              ) : (
                <p className="sd-note sd-pad">この店舗には順位の自動計測がありません。キーワードの登録と、毎日の自動計測（outcomecopies）の対象かを確認してください。</p>
              )}
            </div>
          </div>

          <div className="sd-card">
            <header><h2>成果の時系列推移と対策履歴</h2><span className="sd-sub">グラフか下の対策アイコンをクリックすると、右の詳細が切り替わります</span></header>
            {hasRank && (
              <div className="sd-kwchips">
                <span className="sd-note">グラフに出すKW：</span>
                {[['avg', '平均', AVG_COLOR], ...keywords.map((k, i) => [k.no, `KW${k.no}`, KW_COLORS[i]])].map(([k, l, c]) => (
                  <button key={k} type="button" className="sd-kwchip" style={{ '--kc': c }} aria-pressed={kws.includes(k)} onClick={() => toggleKw(k)}>
                    <i />{l}
                  </button>
                ))}
              </div>
            )}
            <div className="sd-legend">
              <span><i className="sd-lg-line" />確定値</span>
              <span><i className="sd-lg-dash" />暫定値（GBPの確定待ち）</span>
              <span><i className="sd-lg-cmp" />比較：{comparison.label}</span>
              <span><i className="sd-lg-band" />対策実施日</span>
              {/* 分析メモ（2026-10-16 保留）
              <span><i className="sd-lg-memo" />外部要因メモ</span>
              */}
              <span>順位は1位が上</span>
            </div>
            <div className="sd-body">
              <Chart own={own} keywords={keywords} kws={kws} mode={mode} from={from} to={to} dates={dates}
                     comparison={comparison} measureList={measureList} noteList={noteList} kindOf={kindOf}
                     selected={selected} setSel={setSel} hover={hover} setHover={setHover} hasRank={hasRank}
                     provisionalIndex={provisionalIndex} outOfRank={outOfRank} END={END} firstKw={firstKw} />
            </div>
          </div>
        </div>

        <Detail own={own} industry={industry} selected={selected} dates={dates} measureList={measureList}
                noteList={noteList} windows={windows} kindOf={kindOf} beforeAfter={beforeAfter} flagsOf={flagsOf}
                hasRank={hasRank} firstKw={firstKw} outOfRank={outOfRank} provisionalIndex={provisionalIndex}
                store={store} />
      </div>

      <div className="sd-card sd-mt">
        <header>
          <h2>対策履歴一覧</h2><span className="sd-sub">前後の変化は日平均。評価窓は種別ごとに変えられます</span>
          <div className="sd-tools"><button type="button" className="sd-btn sd-small" onClick={() => setSettingsOpen('win')}>⚙ 評価窓の設定</button></div>
        </header>
        <div className="sd-body sd-flush">
          <HistoryTable own={own} industry={industry} inRange={inRange} windows={windows} kindOf={kindOf}
                        beforeAfter={beforeAfter} flagsOf={flagsOf} selected={selected} hasRank={hasRank}
                        onSelect={(d) => { setSel(d); const el = document.getElementById('sd-chart'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                        dates={dates} agreementUrl={urls.agreement} onMemo={setMemoOf} />
        </div>
      </div>

      {memoOf && <MemoDialog key={memoOf.id} measure={memoOf} url={urls.memo} onClose={() => setMemoOf(null)} />}
      {settingsOpen && (
        <Settings focus={settingsOpen} onClose={() => setSettingsOpen(null)} kpi={kpi} cmp={cmp} kws={kws}
                  windows={windows} kinds={kinds} keywords={keywords} store={store} dates={dates} END={END}
                  from={from} to={to} hasRank={hasRank}
                  onApply={(v) => {
                    setKpi(v.kpi); setCmp(v.cmp); if (v.kws.length) setKws(v.kws); setWindows(v.windows);
                    setSettingsOpen(null); setToast('表示設定を変えました');
                  }} />
      )}
      {starOpen && (
        <StarDialog store={store} star={store.star} kinds={starKinds} url={urls.star} onClose={() => setStarOpen(false)}
                    onSaved={(star) => { setStore((s) => ({ ...s, star })); setStarOpen(false); setToast(star ? '★を保存しました' : '★を外しました'); }} />
      )}
      {toast && <div className="sd-toast" role="status">{toast}</div>}
    </div>
  );
}

// ---- 店舗の見出し -----------------------------------------------------------

function StoreHead({ store, onStar }) {
  return (
    <div className="sd-storehead">
      <StarButton star={store.star} onClick={onStar} />
      <h1>{store.name}</h1>
      {store.code && <span className="sd-pill">{store.code}</span>}
      <div className="sd-meta">
        <span><b>契約会社</b>{store.company || '—'}{store.company ? `（${store.companyStores}店）` : ''}</span>
        <span><b>業種</b>{store.industry || '—'}</span>
        <span><b>エリア</b>{store.prefecture || '—'}</span>
        <span><b>担当</b>{store.staff || '—'}</span>
        <span><b>契約更新</b>{store.renewal ? ymd(store.renewal) : '—'}</span>
        {store.latestMetricDate && <span><b>インサイトの最新</b>{ymd(store.latestMetricDate)}</span>}
      </div>
      {store.star && (
        <div className="sd-star-note">
          <b>★ {store.star.kindLabel}</b>{store.star.memo || '（理由の入力なし）'}
          <span className="sd-sub">{[store.star.by, store.star.at].filter(Boolean).join('・')}</span>
        </div>
      )}
    </div>
  );
}

// ---- 上部の数字 -------------------------------------------------------------

function Kpis({ own, hasRank, firstKw, keywords, kpiRange, comparison, cmpValue, measureList, from, to, outOfRank, provisionalIndex }) {
  const [a, b] = kpiRange; const len = b - a + 1;
  const cur = {
    avg: meanR(own.kwavg, a, b), kw1: meanR(own.kw1, a, b),
    uu: sumR(own.uu, a, b), act: sumR(own.act, a, b),
    call: sumR(own.call, a, b), route: sumR(own.route, a, b), site: sumR(own.site, a, b)
  };
  cur.rate = cur.uu ? (cur.act / cur.uu) * 100 : null;
  const cv = {
    avg: cmpValue('kwavg', a, b), kw1: cmpValue('kw1', a, b),
    uu: cmpValue('uu', a, b, 'sum'), act: cmpValue('act', a, b, 'sum')
  };
  cv.rate = cv.uu ? (cv.act / cv.uu) * 100 : null;
  const cmpText = (v) => (comparison.na ? `比較：${comparison.na}` : `比較：${v}`);
  const measured = countR(own.uu, a, b);
  const provisional = provisionalIndex !== null && b >= provisionalIndex;
  const nm = measureList.filter((m) => m.d >= a && m.d <= b).length;
  const nmR = measureList.filter((m) => m.d >= from && m.d <= to).length;

  const card = (color, label, tip, value, unit, delta, sub) => (
    <div className="sd-kpi" style={{ '--k': color }}>
      <div className="sd-lbl">{label} <span className="sd-qm" title={tip}>?</span></div>
      <div className="sd-row"><div className="sd-val">{value}<small>{unit}</small></div><div>{delta}</div></div>
      <div className="sd-cmp">{sub}</div>
    </div>
  );

  return (
    <div className="sd-kpis">
      {card('#2a78d6', '平均順位', `すべてのKW（${keywords.map((k) => `KW${k.no}`).join('・') || 'なし'}）の順位の平均。圏外は${outOfRank}位として計算します`,
        hasRank ? fx(cur.avg) : '—', hasRank ? '位' : '',
        hasRank && isNum(cv.avg) ? <DeltaRank value={cv.avg - cur.avg} /> : null,
        hasRank ? cmpText(isNum(cv.avg) ? `${fx(cv.avg)}位` : 'データがありません') : '順位の計測がありません')}
      {card('#0d366b', 'KW1の順位', firstKw ? `最優先のキーワード「${firstKw.name}」の順位` : '最優先のキーワードの順位',
        hasRank ? rankText(cur.kw1, outOfRank).replace('位', '') : '—', hasRank && isNum(cur.kw1) && cur.kw1 < outOfRank - 0.05 ? '位' : '',
        hasRank && isNum(cv.kw1) ? <DeltaRank value={cv.kw1 - cur.kw1} /> : null,
        hasRank ? cmpText(isNum(cv.kw1) ? rankText(cv.kw1, outOfRank) : 'データがありません') : '順位の計測がありません')}
      {card('#1baf7a', 'UU数', `期間の合計（取れている${measured}日の合計）。GBP の表示回数（検索・マップ × スマホ・PC）の合計です。${provisional ? '直近は GBP の確定待ち（暫定値）を含み、あとから増えます。' : ''}`,
        fmtN(cur.uu), '',
        isNum(cv.uu) && cur.uu !== null ? <DeltaPct value={pct(cv.uu, cur.uu)} /> : null,
        <>{cmpText(isNum(cv.uu) ? fmtN(cv.uu) : 'データがありません')}<br />日平均 {measured ? fx(cur.uu / measured) : '—'}{provisional ? '（暫定値を含む）' : ''}</>)}
      {card('#4a3aa7', 'アクション数', '電話・経路検索・サイトへのアクセスの合計',
        fmtN(cur.act), '',
        isNum(cv.act) && cur.act !== null ? <DeltaPct value={pct(cv.act, cur.act)} /> : null,
        <>電話 {fmtN(cur.call)} ・ 経路 {fmtN(cur.route)} ・ サイト {fmtN(cur.site)}<br />率 {fx(cur.rate)}%{isNum(cv.rate) ? `（比較 ${fx(cv.rate)}%）` : ''}</>)}
      {card('#eb6834', '対策実施数', '上の数字と同じ期間に実施した対策の件数（1つの履歴に写真と最新情報があれば2件）',
        String(nm), '件', null, `表示期間では ${nmR}件`)}
    </div>
  );
}

// ---- KW別の順位 -------------------------------------------------------------

function weeklyMeans(arr, a, b) {
  const out = [];
  for (let w = a; w <= b; w += 7) out.push(meanR(arr, w, Math.min(w + 6, b)));
  return out;
}

function Spark({ values, color }) {
  const w = 130; const h = 24;
  const v = values.filter(isNum);
  if (v.length < 2) return null;
  let lo = Math.min(...v); let hi = Math.max(...v);
  if (hi === lo) { hi += 1; lo -= 1; }
  const X = (i) => 2 + (i * (w - 6)) / (values.length - 1);
  const Y = (x) => 3 + ((x - lo) / (hi - lo)) * (h - 6);   // 順位なので小さいほど上
  let d = '';
  values.forEach((x, i) => { if (isNum(x)) d += `${d ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(x).toFixed(1)}`; });
  let li = values.length - 1;
  while (li > 0 && !isNum(values[li])) li -= 1;
  return (
    <svg className="sd-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx={X(li)} cy={Y(values[li])} r="2.2" fill={color} />
    </svg>
  );
}

function KwTable({ own, keywords, kws, toggleKw, kpi, kpiRange, comparison, cmpValue, from, to, outOfRank }) {
  const [a, b] = kpiRange;
  const row = (k, label, text, arr, cmpKey, color, primary) => {
    const cur = meanR(arr, a, b);
    const cv = cmpKey ? cmpValue(cmpKey, a, b) : null;
    const best = minR(arr, from, to);
    const on = kws.includes(k);
    return (
      <tr key={k} className={on ? 'sd-on' : ''}>
        <td className="sd-kwn">{label}{primary && <span className="sd-pri">最優先</span>}</td>
        <td>{text}</td>
        <td className="sd-num"><b>{rankText(cur, outOfRank)}</b></td>
        <td className="sd-num">{isNum(cv) ? rankText(cv, outOfRank) : <span className="sd-note">—</span>}</td>
        <td className="sd-num">{isNum(cv) && isNum(cur) ? <DeltaRank value={cv - cur} /> : ''}</td>
        <td><Spark values={weeklyMeans(arr, from, to)} color={color} /></td>
        <td className="sd-num">{best === null ? '—' : rankText(Math.round(best), outOfRank)}</td>
        <td className="sd-c"><input type="checkbox" checked={on} onChange={() => toggleKw(k)} aria-label={`${label}をグラフに出す`} /></td>
      </tr>
    );
  };
  // ほかの店舗と比べるときは、KWが店ごとに違うので KW1 と平均だけ比べる。
  const cmpKeyOf = (k, i) => {
    if (comparison.na) return null;
    if (comparison.store) return i === 0 ? 'kw1' : `kw${k.no}`;
    return i === 0 ? 'kw1' : null;
  };

  return (
    <div className="sd-tbl-wrap">
      <table className="sd-table sd-kwtbl">
        <thead>
          <tr>
            <th>KW</th><th>キーワード</th><th className="sd-num">{KPI_LABEL[kpi]}</th>
            <th className="sd-num">比較（{comparison.label}）</th><th className="sd-num">差</th>
            <th>推移（表示期間・週ごと）</th><th className="sd-num">期間中の最高</th><th className="sd-c">グラフ</th>
          </tr>
        </thead>
        <tbody>
          {keywords.map((k, i) => row(k.no, `KW${k.no}`, k.name, own.kw[k.no], cmpKeyOf(k, i), KW_COLORS[i], i === 0))}
          {row('avg', '平均', 'すべてのKWの平均', own.kwavg, comparison.na ? null : 'kwavg', AVG_COLOR, false)}
        </tbody>
      </table>
      {!comparison.na && !comparison.store && (
        <p className="sd-note sd-pad">ほかの店舗と比べるときは、KWが店舗ごとに違うため、KW1と平均だけを比べます。</p>
      )}
    </div>
  );
}

// ---- グラフ -----------------------------------------------------------------

function Chart({ own, keywords, kws, mode, from, to, dates, comparison, measureList, noteList, kindOf, selected, setSel, hover, setHover, hasRank, provisionalIndex, outOfRank, END, firstKw }) {
  // 横幅は枠の実際の幅に合わせる。viewBox を固定にすると、広い画面で文字まで拡大されて
  // 目盛りの字が本文より大きくなってしまう。
  const wrapRef = useRef(null);
  const [W, setW] = useState(820);
  // 最初の描画の前に測る（useLayoutEffect）。測る前の幅で一度描くと文字が大きく出てしまう。
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (el && el.clientWidth) setW(Math.max(560, Math.round(el.clientWidth)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const a = from; const b = to; const n = b - a + 1;
  const ml = 58; const mr = 92; const x0 = ml; const x1 = W - mr;
  const step = n > 1 ? (x1 - x0) / (n - 1) : 0;
  const X = (i) => x0 + (n > 1 ? (i - a) * step : (x1 - x0) / 2);
  const RK = hasRank ? { t: 24, h: 126 } : null;
  const top = hasRank ? 24 : 24;
  const UU = { t: hasRank ? 184 : 24, h: 96 };
  const AC = { t: UU.t + 130, h: 76 };
  const AX = AC.t + AC.h + 16; const LN = AX + 26; const H = LN + 24;
  const bot = AC.t + AC.h;

  const ma = mode === 'ma';
  const view = useMemo(() => ({
    uu: ma ? movingAverage(own.uu) : own.uu,
    act: ma ? movingAverage(own.act) : own.act,
    kwavg: ma ? movingAverage(own.kwavg) : own.kwavg,
    kw: Object.fromEntries(keywords.map((k) => [k.no, ma ? movingAverage(own.kw[k.no]) : own.kw[k.no]]))
  }), [own, keywords, ma]);

  // 比べる相手の線（灰色）。同じ日付に揃えて取り出す。
  const overlay = useMemo(() => {
    const C = comparison;
    if (C.na || !C.source) return null;
    const get = (key) => {
      const src = C.source[key];
      if (!src) return null;
      if (C.flat) { const m = meanR(src, 0, END); return Array(n).fill(m); }
      const arr = ma ? movingAverage(src) : src;
      return Array.from({ length: n }, (_, j) => { const i = a + j + C.off; return i >= 0 && i <= END ? arr[i] : null; });
    };
    return { uu: get('uu'), act: get('act'), kw1: get('kw1'), kwavg: get('kwavg') };
  }, [comparison, ma, a, n, END]);

  let um = 1; let am = 1;
  for (let i = a; i <= b; i += 1) {
    if (isNum(view.uu[i])) um = Math.max(um, view.uu[i]);
    if (isNum(view.act[i])) am = Math.max(am, view.act[i]);
  }
  if (overlay) {
    (overlay.uu || []).forEach((v) => { if (isNum(v)) um = Math.max(um, v); });
    (overlay.act || []).forEach((v) => { if (isNum(v)) am = Math.max(am, v); });
  }
  um = niceMax(um * 1.08); am = niceMax(am * 1.08);
  const yU = (v) => UU.t + UU.h * (1 - v / um);
  const yA = (v) => AC.t + AC.h * (1 - v / am);
  // 順位は平方根の目盛り。上位（1〜5位）の動きを大きく見せる。
  const yR = (r) => RK.t + RK.h * Math.sqrt((Math.min(Math.max(r, 1), outOfRank) - 1) / (outOfRank - 1));
  const bw = Math.max(step, 3);

  const path = (arr, Y, s, e) => {
    let d = ''; let pen = false;
    for (let i = s; i <= e; i += 1) {
      const v = arr[i];
      if (!isNum(v)) { pen = false; continue; }
      d += `${pen ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };
  const pathRel = (arr, Y) => {
    let d = ''; let pen = false;
    arr.forEach((v, j) => {
      if (!isNum(v)) { pen = false; return; }
      d += `${pen ? 'L' : 'M'}${X(a + j).toFixed(1)} ${Y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };
  const lastIndex = (arr) => { let i = b; while (i >= a && !isNum(arr[i])) i -= 1; return i >= a ? i : null; };

  // 目盛り（横軸）
  const tickStep = [1, 2, 7, 14, 28, 61, 92, 183].find((s) => n / s <= 8) || 183;
  const ticks = [];
  for (let i = a; i <= b; i += tickStep) {
    const lab = i === a && dates[i].slice(0, 4) !== dates[END].slice(0, 4) ? `${dates[i].slice(0, 4)}/${md(dates[i])}` : md(dates[i]);
    ticks.push(<text key={i} x={X(i)} y={AX} textAnchor="middle">{lab}</text>);
  }

  // 右端のラベル（重ならないようにずらす）
  const ends = [];
  if (hasRank) {
    kws.forEach((k) => {
      const isAvg = k === 'avg';
      const arr = isAvg ? view.kwavg : view.kw[k];
      if (!arr) return;
      const li = lastIndex(arr);
      if (li === null) return;
      const idx = keywords.findIndex((x) => x.no === k);
      ends.push({ y: yR(arr[li]), cy: yR(arr[li]), cx: X(li), col: isAvg ? AVG_COLOR : KW_COLORS[idx], t: `${isAvg ? '平均' : `KW${k}`} ${rankText(Math.round(arr[li] * 10) / 10, outOfRank)}` });
    });
    ends.sort((p, q) => p.y - q.y);
    for (let i = 1; i < ends.length; i += 1) if (ends[i].y < ends[i - 1].y + 12) ends[i].y = ends[i - 1].y + 12;
    const over = ends.length ? ends[ends.length - 1].y - (RK.t + RK.h) : 0;
    if (over > 0) ends.forEach((e) => { e.y -= over; });
  }

  const lineWithProvisional = (arr, Y, decimals) => {
    const out = [];
    const conf = provisionalIndex === null ? b : Math.min(b, provisionalIndex);
    if (conf >= a) out.push(<path key="c" d={path(arr, Y, a, conf)} fill="none" stroke="#343a40" strokeWidth="2" strokeLinejoin="round" />);
    if (provisionalIndex !== null && b > provisionalIndex) {
      out.push(<path key="p" d={path(arr, Y, Math.max(a, provisionalIndex), b)} fill="none" stroke="#343a40" strokeWidth="2" strokeDasharray="4 3" />);
    }
    const li = lastIndex(arr);
    if (li !== null) {
      out.push(<circle key="dot" cx={X(li)} cy={Y(arr[li])} r="3.2" fill="#343a40" />);
      out.push(<text key="lab" x={X(li) + 8} y={Y(arr[li]) + 4} className="sd-t-ink" style={{ fontSize: 11, fontWeight: 700 }}>{decimals ? fx(arr[li], 1) : fmtN(arr[li])}</text>);
    }
    return out;
  };

  const gridR = hasRank ? [1, 3, 5, 10, 15, outOfRank] : [];
  const tl = (y, t) => <text x="0" y={y} className="sd-t-ink" style={{ fontWeight: 700, fontSize: 11.5 }}>{t}</text>;
  const tk = (v, max) => (max < 10 ? fx(v, 1) : fmtN(v));

  const visibleNotes = noteList.filter((nt) => nt.b >= a && nt.a <= b);
  const visibleMeasures = measureList.filter((m) => m.d >= a && m.d <= b);
  const byDay = {};
  visibleMeasures.forEach((m) => { (byDay[m.d] = byDay[m.d] || []).push(m); });

  const hoverRects = [];
  for (let i = a; i <= b; i += 1) {
    hoverRects.push(
      <rect key={i} x={X(i) - Math.max(step, 2) / 2} y={top - 4} width={Math.max(step, 2)} height={LN - top + 16}
            fill="transparent" style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => setSel(i)} />
    );
  }

  return (
    <div className="sd-chart-wrap" id="sd-chart" ref={wrapRef}>
      <svg className="sd-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="順位・UU数・アクション数の推移と対策の実施日">
        <defs>
          <pattern id="sd-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="#f8f9fa" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#ced4da" strokeWidth="2" />
          </pattern>
        </defs>

        {visibleNotes.map((nt) => {
          const xa = X(Math.max(nt.a, a)) - bw / 2; const xb = X(Math.min(nt.b, b)) + bw / 2;
          const lab = xb - xa > 90 ? (nt.title.length > 12 ? `${nt.title.slice(0, 11)}…` : nt.title) : 'メモ';
          return (
            <g key={`n${nt.id}`}>
              <rect x={xa} y={top} width={xb - xa} height={bot - top} fill="url(#sd-hatch)" />
              <text x={(xa + xb) / 2} y={top - 6} textAnchor="middle" className="sd-t-ink2" style={{ fontSize: 10 }}>{lab}</text>
            </g>
          );
        })}
        {visibleMeasures.map((m) => (
          <rect key={`b${m.id}`} x={X(m.d) - bw / 2} y={top} width={bw} height={bot - top} fill={kindOf[m.kind].color} opacity=".15" />
        ))}

        {hasRank && tl(RK.t - 10, '順位')}
        {tl(UU.t - 12, 'UU数')}
        {tl(AC.t - 12, 'アクション数')}

        {gridR.map((r) => (
          <g key={`gr${r}`}>
            <line className="sd-grid" x1={x0} x2={x1} y1={yR(r)} y2={yR(r)} />
            <text x={x0 - 6} y={yR(r) + 3.5} textAnchor="end">{r === outOfRank ? '圏外' : `${r}位`}</text>
          </g>
        ))}
        {[0, um / 2, um].map((v) => (
          <g key={`gu${v}`}>
            <line className="sd-grid" x1={x0} x2={x1} y1={yU(v)} y2={yU(v)} />
            <text x={x0 - 6} y={yU(v) + 3.5} textAnchor="end">{tk(v, um)}</text>
          </g>
        ))}
        {[0, am / 2, am].map((v) => (
          <g key={`ga${v}`}>
            <line className="sd-grid" x1={x0} x2={x1} y1={yA(v)} y2={yA(v)} />
            <text x={x0 - 6} y={yA(v) + 3.5} textAnchor="end">{tk(v, am)}</text>
          </g>
        ))}

        {overlay && (
          <g fill="none" stroke={CMP_COLOR} strokeWidth="1.6">
            {hasRank && (() => {
              const rk = kws.includes('avg') ? overlay.kwavg : overlay.kw1;
              return rk ? <path d={pathRel(rk, yR)} /> : null;
            })()}
            {overlay.uu && <path d={pathRel(overlay.uu, yU)} />}
            {overlay.act && <path d={pathRel(overlay.act, yA)} />}
          </g>
        )}

        {hasRank && kws.map((k) => {
          const isAvg = k === 'avg';
          const arr = isAvg ? view.kwavg : view.kw[k];
          if (!arr) return null;
          const idx = keywords.findIndex((x) => x.no === k);
          return <path key={`kw${k}`} d={path(arr, yR, a, b)} fill="none" stroke={isAvg ? AVG_COLOR : KW_COLORS[idx]} strokeWidth={isAvg ? 1.8 : KW_WIDTHS[idx]} strokeLinejoin="round" />;
        })}
        {ends.map((e) => (
          <g key={`e${e.t}`}>
            <circle cx={e.cx} cy={e.cy} r="3" fill={e.col} />
            <line x1={x1 + 5} x2={x1 + 12} y1={e.y} y2={e.y} stroke={e.col} strokeWidth="2" />
            <text x={x1 + 15} y={e.y + 3.5} className="sd-t-ink2" style={{ fontSize: 10.5 }}>{e.t}</text>
          </g>
        ))}

        {lineWithProvisional(view.uu, yU, ma)}
        {lineWithProvisional(view.act, yA, true)}

        {selected >= a && selected <= b && (
          <line x1={X(selected)} x2={X(selected)} y1={top - 2} y2={LN} stroke="#1c7ed6" strokeWidth="1.5" />
        )}
        {hover !== null && hover !== selected && (
          <line x1={X(hover)} x2={X(hover)} y1={top - 2} y2={bot} stroke="rgba(33,37,41,.35)" strokeWidth="1" />
        )}
        <line x1={x0} x2={x1} y1={bot} y2={bot} stroke="#dee2e6" />
        {ticks}
        {tl(LN + 4, '対策')}
        <line x1={x0} x2={x1} y1={LN} y2={LN} stroke="#e9ecef" />

        {hoverRects}

        {Object.entries(byDay).map(([d, list]) => {
          const day = Number(d); const k = kindOf[list[0].kind]; const x = X(day); const on = day === selected;
          return (
            <g key={`m${d}`} className="sd-mk" onClick={() => setSel(day)} onMouseEnter={() => setHover(day)} onMouseLeave={() => setHover(null)}>
              <circle cx={x} cy={LN} r={on ? 10.5 : 9} fill="#fff" stroke={on ? '#1c7ed6' : '#fff'} strokeWidth="2" />
              <circle cx={x} cy={LN} r="7.5" fill={k.color} />
              <text x={x} y={LN + 3.5} textAnchor="middle" style={{ fill: '#fff', fontSize: 9.5, fontWeight: 700 }}>{k.short}</text>
              {list.length > 1 && <text x={x + 10} y={LN - 6} style={{ fontSize: 9, fontWeight: 700 }} className="sd-t-ink2">+{list.length - 1}</text>}
            </g>
          );
        })}
      </svg>

      {hover !== null && (
        <DayTip i={hover} left={(X(hover) / W) * 100} own={own} dates={dates} byDay={byDay} noteList={noteList}
                hasRank={hasRank} outOfRank={outOfRank} provisionalIndex={provisionalIndex} firstKw={firstKw} />
      )}
    </div>
  );
}

function DayTip({ i, left, own, dates, byDay, noteList, hasRank, outOfRank, provisionalIndex, firstKw }) {
  const ms = byDay[i] || [];
  const memos = noteList.filter((nt) => nt.a <= i && nt.b >= i);
  const style = left > 60 ? { right: `${100 - left + 1.5}%` } : { left: `${left + 1.5}%` };
  return (
    <div className="sd-tip" style={style}>
      <div className="sd-tip-t">{dayLabel(dates[i])}</div>
      {hasRank && <div className="sd-tip-r"><span>KW1の順位{firstKw ? `（${firstKw.name}）` : ''}</span><b>{rankText(own.kw1[i], outOfRank)}</b></div>}
      {hasRank && <div className="sd-tip-r"><span>平均順位</span><b>{rankText(own.kwavg[i], outOfRank)}</b></div>}
      <div className="sd-tip-r"><span>UU数</span><b>{fmtN(own.uu[i])}{provisionalIndex !== null && i >= provisionalIndex && isNum(own.uu[i]) ? '（暫定）' : ''}</b></div>
      <div className="sd-tip-r"><span>アクション数</span><b>{fmtN(own.act[i])}</b></div>
      {ms.length > 0 && <div className="sd-tip-x">対策：{ms.map((m) => m.kind).join('、')}</div>}
      {memos.length > 0 && <div className="sd-tip-x sd-muted">メモ：{memos.map((nt) => nt.title).join('、')}</div>}
    </div>
  );
}

// ---- 右の詳細 ---------------------------------------------------------------

function BaCell({ label, o, kind, outOfRank }) {
  if (!isNum(o.before)) return <div><div className="sd-k">{label}</div><div className="sd-v">—</div></div>;
  const fmt = (v) => (kind === 'rk' ? rankText(Math.round(v * 10) / 10, outOfRank) : fx(v));
  if (!o.done) {
    return (
      <div>
        <div className="sd-k">{label}</div>
        <div className="sd-v">{fmt(o.before)}→</div>
        <div className="sd-d sd-note">評価中<br />あと{o.left}日</div>
      </div>
    );
  }
  return (
    <div>
      <div className="sd-k">{label}</div>
      <div className="sd-v">{fmt(o.before)}→{isNum(o.after) ? fmt(o.after) : '—'}</div>
      <div className="sd-d">{kind === 'rk' ? <DeltaRank value={o.before - o.after} /> : <DeltaPct value={pct(o.before, o.after)} />}</div>
    </div>
  );
}

function Detail({ own, industry, selected, dates, measureList, noteList, windows, kindOf, beforeAfter, flagsOf, hasRank, firstKw, outOfRank, provisionalIndex, store }) {
  const ms = measureList.filter((m) => m.d === selected);
  // 分析メモ（2026-10-16 保留）。再開するときに下の「この日にかかる外部要因メモ」と一緒に戻す
  // const memos = noteList.filter((nt) => nt.a <= selected && nt.b >= selected);
  return (
    <aside className="sd-card sd-detail">
      <div className="sd-hd">選択日：{dayLabel(dates[selected])} の対策詳細</div>
      <section>
        {!ms.length && <p className="sd-note" style={{ margin: 0 }}>この日の対策はありません。グラフ下の対策アイコンを選ぶと、ここに詳細が出ます。</p>}
        {ms.map((m) => {
          const w = windows[m.kind];
          const k1 = beforeAfter(own.kw1, m.d, w);
          const uu = beforeAfter(own.uu, m.d, w);
          const ac = beforeAfter(own.act, m.d, w);
          const iu = industry ? beforeAfter(industry.uu, m.d, w) : null;
          const flags = flagsOf(m, w);
          const diff = iu && uu.done ? pct(uu.before, uu.after) - pct(iu.before, iu.after) : null;
          return (
            <div className="sd-mcard" key={m.id}>
              <div className="sd-ttl"><KindChip kind={kindOf[m.kind]} />{m.count ? <span className="sd-note">件数 {m.count}</span> : null}</div>
              <div style={{ fontWeight: 700 }}>{m.details.join('、')}</div>
              {m.memo && <div className="sd-note sd-memo-clamp" title={m.memo}>{m.memo}</div>}
              <div className="sd-note">対策後の変化（前{w.pre}日と後{w.post}日の日平均を比べる）</div>
              <div className="sd-ba">
                {hasRank ? <BaCell label="KW1の順位" o={k1} kind="rk" outOfRank={outOfRank} /> : <div><div className="sd-k">KW1の順位</div><div className="sd-v">—</div></div>}
                <BaCell label="UU数" o={uu} kind="tr" />
                <BaCell label="アクション数" o={ac} kind="tr" />
              </div>
              {isNum(diff) && (
                <div className="sd-note">
                  同業種（{store.industry}）は同じ期間に {sgn(pct(iu.before, iu.after))}%。差し引くと
                  <b className={diff >= 0 ? 'sd-d-up' : 'sd-d-down'}> {sgn(diff)}pt</b>
                </div>
              )}
              {flags.length > 0 && <div className="sd-flag">{flags.map((f) => <div key={f}>{f}</div>)}</div>}
            </div>
          );
        })}
      </section>
      {/* 分析メモ（2026-10-16 保留）
      <section>
        <h3>この日にかかる外部要因メモ</h3>
        {memos.length ? (
          <div className="sd-memo-mini">
            {memos.map((nt) => (
              <div key={nt.id}>
                {nt.title}
                <div className="sd-m">{nt.type} ・ {ymd(nt.start)}{nt.end > nt.start ? `〜${md(nt.end)}` : ''} ・ {nt.scope} ・ {nt.excluded ? '分析から除外' : '分析に含める'}</div>
              </div>
            ))}
          </div>
        ) : <p className="sd-note" style={{ margin: '0 0 8px' }}>ありません</p>}
        <a className="sd-btn sd-small" href={noteHref(selected)}>＋ この日のメモを登録</a>
      </section>
      */}
      <section>
        <h3>この日の数字（実測）</h3>
        <div className="sd-ba">
          <div><div className="sd-k">KW1の順位</div><div className="sd-v">{hasRank ? rankText(own.kw1[selected], outOfRank) : '—'}</div></div>
          <div><div className="sd-k">UU数</div><div className="sd-v">{fmtN(own.uu[selected])}{provisionalIndex !== null && selected >= provisionalIndex && isNum(own.uu[selected]) ? <span className="sd-note">（暫定）</span> : null}</div></div>
          <div><div className="sd-k">アクション数</div><div className="sd-v">{fmtN(own.act[selected])}</div></div>
        </div>
        {firstKw && hasRank && <p className="sd-note" style={{ margin: '6px 0 0' }}>KW1：{firstKw.name}</p>}
      </section>
    </aside>
  );
}

function KindChip({ kind }) {
  return <span className="sd-chip" style={{ '--c': kind.color }}><b>{kind.short}</b>{kind.label}</span>;
}

// ---- 対策履歴一覧 -----------------------------------------------------------

function BaText({ o, kind, outOfRank }) {
  if (!isNum(o.before)) return '—';
  if (!o.done) return <span className="sd-note">評価中<br />あと{o.left}日</span>;
  const fmt = (v) => (kind === 'rk' ? fx(v) : fx(v));
  return (
    <>
      {fmt(o.before)}→{isNum(o.after) ? fmt(o.after) : '—'}
      <span className="sd-sub">{kind === 'rk' ? <DeltaRank value={o.before - o.after} /> : <DeltaPct value={pct(o.before, o.after)} />}</span>
    </>
  );
}

function HistoryTable({ own, industry, inRange, windows, kindOf, beforeAfter, flagsOf, selected, hasRank, onSelect, dates, agreementUrl, onMemo }) {
  const rows = inRange.slice().reverse();
  return (
    <div className="sd-tbl-wrap sd-history">
      <table className="sd-table">
        <thead>
          <tr>
            <th>日付</th><th>種別</th><th>実施内容</th><th className="sd-num">件数</th><th className="sd-num">評価窓</th>
            <th className="sd-num">KW1の順位</th><th className="sd-num">平均順位</th><th className="sd-num">UU数</th>
            <th className="sd-num">アクション数</th><th className="sd-num">同業種との差<span className="sd-sub">UU数</span></th><th>備考</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => {
            const w = windows[m.kind];
            const k = kindOf[m.kind];
            const changed = w.pre !== k.window || w.post !== k.window;
            const k1 = beforeAfter(own.kw1, m.d, w);
            const av = beforeAfter(own.kwavg, m.d, w);
            const uu = beforeAfter(own.uu, m.d, w);
            const ac = beforeAfter(own.act, m.d, w);
            const iu = industry ? beforeAfter(industry.uu, m.d, w) : null;
            const diff = iu && uu.done ? pct(uu.before, uu.after) - pct(iu.before, iu.after) : null;
            return (
              <tr key={m.id} className={`sd-row-click ${m.d === selected ? 'sd-sel' : ''}`} onClick={() => onSelect(m.d)}>
                <td>{ymd(dates[m.d])}</td>
                <td><KindChip kind={k} /></td>
                <td>
                  <div className="sd-detail-cell">
                    <span>{m.details.join('、')}</span>
                    {/* 行のクリック（グラフの日付を選ぶ）と分ける */}
                    {/* 詳細・修正理由が空の履歴は押せなくする（開いて空、を避ける） */}
                    <button type="button" className="sd-btn sd-small sd-ghost sd-more" disabled={!m.memo}
                            title={m.memo ? '詳細・修正理由を見る' : '詳細・修正理由は入力されていません'}
                            onClick={(e) => { e.stopPropagation(); onMemo(m); }}>詳細</button>
                  </div>
                </td>
                <td className="sd-num">{m.count || '—'}</td>
                <td className="sd-num">前{w.pre}／後{w.post}日{changed && <span className="sd-sub" style={{ color: '#1c7ed6' }}>設定で変更</span>}</td>
                <td className="sd-num">{hasRank ? <BaText o={k1} kind="rk" /> : '—'}</td>
                <td className="sd-num">{hasRank ? <BaText o={av} kind="rk" /> : '—'}</td>
                <td className="sd-num"><BaText o={uu} kind="tr" /></td>
                <td className="sd-num"><BaText o={ac} kind="tr" /></td>
                <td className="sd-num">{isNum(diff) ? <DeltaPct value={diff} unit="pt" /> : <span className="sd-note">—</span>}</td>
                <td className="sd-note">{flagsOf(m, w).map((f) => <div key={f}>{f}</div>)}</td>
              </tr>
            );
          })}
          {!rows.length && (
            <tr><td colSpan="11" className="sd-note">表示期間に対策はありません。表示期間を広げるか、<a href={agreementUrl}>案件詳細</a>から対応履歴（対策履歴）を入力してください。</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- 詳細・修正理由 ---------------------------------------------------------
// 対策履歴一覧の「＋」から開く。本文は長いので最初の props には入れず、開いたときに取りに行く。

function MemoDialog({ measure, url, onClose }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    fetch(`${url}${measure.recordId}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((res) => res.json().then((json) => ({ ok: res.ok, json })))
      .then(({ ok, json }) => { if (alive) setState(ok ? { data: json } : { error: json.reason || '読み込めませんでした' }); })
      .catch(() => { if (alive) setState({ error: '読み込めませんでした' }); });
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { alive = false; window.removeEventListener('keydown', onKey); };
  }, [measure.recordId]);

  const { data, error } = state;
  return (
    <>
      <div className="sd-ov" onClick={onClose} />
      <div className="sd-dialog" role="dialog" aria-modal="true" aria-label="詳細・修正理由">
        <div className="sd-dh">
          <span>詳細・修正理由{data && <span className="sd-sub">{data.date}{data.staff ? `・${data.staff}` : ''}</span>}</span>
          <button type="button" className="sd-x" onClick={onClose} aria-label="閉じる">×</button>
        </div>
        <div className="sd-db">
          {state.loading && <div className="sd-note">読み込み中…</div>}
          {error && <div className="sd-note">{error}</div>}
          {data && (
            <>
              {data.items.length > 0 && (
                <div className="sd-note">{data.items.map((line) => <div key={line}>{line}</div>)}</div>
              )}
              {data.memo ? <div className="sd-memo-full">{data.memo}</div> : <div className="sd-note">詳細・修正理由は入力されていません。</div>}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---- 表示設定 ---------------------------------------------------------------

function Settings({ focus, onClose, onApply, kpi, cmp, kws, windows, kinds, keywords, store, dates, END, from, to, hasRank }) {
  const [vKpi, setVKpi] = useState(kpi);
  const [vCmp, setVCmp] = useState(cmp);
  const [vKws, setVKws] = useState(kws);
  const [vWin, setVWin] = useState(windows);

  useEffect(() => {
    const id = { kpi: 'sd-fs-kpi', win: 'sd-fs-win' }[focus];
    const el = id && document.getElementById(id);
    if (el) el.scrollIntoView({ block: 'start' });
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const end = parseIso(dates[END]);
  const hints = {
    '7d': `${ymd(dates[Math.max(0, END - 6)])}〜${ymd(dates[END])}`,
    '30d': `${ymd(dates[Math.max(0, END - 29)])}〜${ymd(dates[END])}`,
    month: `${end.getUTCFullYear()}/${end.getUTCMonth() + 1}/1〜${ymd(dates[END])}`,
    prevmonth: `${end.getUTCMonth() === 0 ? 12 : end.getUTCMonth()}月`,
    range: `${ymd(dates[from])}〜${ymd(dates[to])}`
  };
  const radio = (name, value, label, cur, set, disabled, hint) => (
    <label key={value} className={disabled ? 'sd-dis' : ''}>
      <input type="radio" name={name} value={value} checked={cur === value} disabled={disabled} onChange={() => set(value)} /> {label}
      {hint && <span className="sd-note">{hint}</span>}
    </label>
  );
  const setWin = (label, side, value) => {
    const v = Math.max(1, Math.min(90, Math.round(Number(value)) || 1));
    setVWin((w) => ({ ...w, [label]: { ...w[label], [side]: v } }));
  };
  const toggle = (k) => setVKws((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  return (
    <>
      <div className="sd-ov" onClick={onClose} />
      <div className="sd-drawer" role="dialog" aria-modal="true" aria-label="表示設定">
        <div className="sd-dh">表示設定<button type="button" className="sd-x" onClick={onClose} aria-label="閉じる">×</button></div>
        <div className="sd-db">
          <fieldset className="sd-fs" id="sd-fs-kpi">
            <legend>上部の数字（順位・UU数・アクション数）を集計する期間</legend>
            <div className="sd-radios">{KPI_PERIODS.map(([v, l]) => radio('sKpi', v, l, vKpi, setVKpi, false, hints[v]))}</div>
          </fieldset>
          <fieldset className="sd-fs">
            <legend>比較の基準</legend>
            <div className="sd-radios">
              <div className="sd-grpl">この店舗の過去と比べる</div>
              {OWN_BASES.map(([v, l, h]) => radio('sCmp', v, l, vCmp, setVCmp, false, h))}
              <div className="sd-grpl">同じ期間のほかの店舗と比べる</div>
              {radio('sCmp', 'industry', `同業種（${store.industry || '業種未登録'}）の平均`, vCmp, setVCmp, !store.industry, store.industry ? '' : '業種が登録されていません')}
              {radio('sCmp', 'company', `同じ契約会社（${store.company || '—'}）の他店舗の平均`, vCmp, setVCmp, store.companyStores < 2, store.companyStores < 2 ? 'この会社は1店舗のみです' : '')}
              {radio('sCmp', 'allstores', '全店舗の平均', vCmp, setVCmp, false)}
            </div>
            <p className="sd-note" style={{ margin: '6px 0 0' }}>比べた値は、グラフにも灰色の線で重ねます。</p>
          </fieldset>
          {hasRank && (
            <fieldset className="sd-fs">
              <legend>グラフに出すKW</legend>
              <div className="sd-radios">
                {[['avg', '平均（すべてのKW）'], ...keywords.map((k) => [k.no, `KW${k.no}　${k.name}`])].map(([k, l]) => (
                  <label key={k}><input type="checkbox" checked={vKws.includes(k)} onChange={() => toggle(k)} /> {l}</label>
                ))}
              </div>
            </fieldset>
          )}
          <fieldset className="sd-fs" id="sd-fs-win">
            <legend>評価窓（対策の種別ごと）</legend>
            <div className="sd-tbl-wrap">
              <table className="sd-table sd-wintbl">
                <thead><tr><th>種別</th><th className="sd-num">前（日）</th><th className="sd-num">後（日）</th><th className="sd-num">初期値</th></tr></thead>
                <tbody>
                  {kinds.map((k) => (
                    <tr key={k.label}>
                      <td><KindChip kind={k} /></td>
                      <td className="sd-num"><input type="number" min="1" max="90" value={vWin[k.label].pre} onChange={(e) => setWin(k.label, 'pre', e.target.value)} aria-label={`${k.label}の前の日数`} /></td>
                      <td className="sd-num"><input type="number" min="1" max="90" value={vWin[k.label].post} onChange={(e) => setWin(k.label, 'post', e.target.value)} aria-label={`${k.label}の後の日数`} /></td>
                      <td className="sd-num sd-note">前後{k.window}日</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="sd-linkbtn" onClick={() => setVWin(Object.fromEntries(kinds.map((k) => [k.label, { pre: k.window, post: k.window }])))}>すべて初期値に戻す</button>
            <p className="sd-note" style={{ margin: '6px 0 0' }}>ここで変えた評価窓は、この画面の表示だけに使います（保存はしません）。</p>
          </fieldset>
        </div>
        <div className="sd-df">
          <button type="button" className="sd-btn sd-ghost" onClick={onClose}>キャンセル</button>
          <button type="button" className="sd-btn sd-primary" onClick={() => onApply({ kpi: vKpi, cmp: vCmp, kws: vKws, windows: vWin })}>反映する</button>
        </div>
      </div>
    </>
  );
}
